// ===============================================
// 🚀 APLICAÇÃO PRINCIPAL - INHOLDING
// ===============================================
// Arquivo: src/App.js
// Versão: 3.3.0 - Com Tela Administrativa

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import RoleBasedRoute from './components/RoleBasedRoute';
import Header from './components/Header';
// Estilos
import './App.css';

// Páginas da aplicação
// ⚠️ Login e AdminPage importados de forma síncrona para evitar ChunkLoadError
//    (AdminPage usa RelatoriosSection que depende de jspdf/jspdf-autotable).
import Login from './pages/Login';
import AdminPage from './pages/AdminPage';
const ExpeditionPage = React.lazy(() => import('./pages/ExpeditionPage'));
const ReceiptPage = React.lazy(() => import('./pages/ReceiptPage'));
const LinhaTempo = React.lazy(() => import('./pages/LinhaTempo'));

// ===============================================
// 🔄 COMPONENTE DE REDIRECIONAMENTO INTELIGENTE
// ===============================================
const SmartRedirect = () => {
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  // Redirecionar baseado no cargo do usuário
  switch (user.cargo?.toLowerCase()) {
    case 'administrador':
      return <Navigate to="/administrator" replace />;
    case 'expedição':
    case 'expedicao':
      return <Navigate to="/expedition" replace />;
    case 'loja':
      return <Navigate to="/receipt" replace />;
    default:
      return <Navigate to="/expedition" replace />;
  }
};

// ===============================================
// 🖥️ CONTEÚDO PRINCIPAL DA APLICAÇÃO
// ===============================================
const AppContent = () => {
  const { isAuthenticated, loading } = useAuth();

  // console.log removido para performance

  if (loading) {
    return null; // evitar renderização pesada ao carregar
  }

  return (
    <div className="App">
      {/* Header - mostrar apenas se autenticado */}
      {isAuthenticated && <Header />}
      
      {/* Conteúdo principal */}
      <main className="main-content">
        <React.Suspense fallback={<div className="route-loading" aria-hidden="true">Carregando...</div>}>
          <Routes>
            {/* =============================================== */}
            {/* 🔓 ROTA PÚBLICA - LOGIN */}
            {/* =============================================== */}
            <Route 
              path="/login" 
              element={
                isAuthenticated ? 
                <SmartRedirect /> : 
                <Login />
              } 
            />
            
            {/* =============================================== */}
            {/* 🏠 ROTA RAIZ - REDIRECIONAMENTO INTELIGENTE */}
            {/* =============================================== */}
            <Route 
              path="/" 
              element={<SmartRedirect />}
            />
            
            {/* =============================================== */}
            {/* 📦 EXPEDIÇÃO - ADMIN + EXPEDIÇÃO */}
            {/* =============================================== */}
            <Route 
              path="/expedition" 
              element={
                <RoleBasedRoute allowedRoles={['administrador', 'expedição', 'expedicao']}>
                  <ExpeditionPage />
                </RoleBasedRoute>
              } 
            />
            
            {/* =============================================== */}
            {/* 🏪 RECEBIMENTO - ADMIN + LOJA */}
            {/* =============================================== */}
            <Route 
              path="/receipt" 
              element={
                <RoleBasedRoute allowedRoles={['administrador', 'loja']}>
                  <ReceiptPage />
                </RoleBasedRoute>
              } 
            />
            
            {/* =============================================== */}
            {/* 🔍 PESQUISA - TODOS OS USUÁRIOS AUTENTICADOS */}
            {/* =============================================== */}
            <Route 
              path="/search" 
              element={
                <ProtectedRoute>
                  <LinhaTempo />
                </ProtectedRoute>
              } 
            />
            
            {/* =============================================== */}
            {/* 👑 ADMINISTRAÇÃO - APENAS ADMINISTRADORES */}
            {/* =============================================== */}
            <Route 
              path="/administrator" 
              element={
                <RoleBasedRoute allowedRoles={['administrador']}>
                  <AdminPage />
                </RoleBasedRoute>
              } 
            />
            
            {/* =============================================== */}
            {/* 🚫 ROTA NÃO ENCONTRADA - REDIRECIONAMENTO */}
            {/* =============================================== */}
            <Route 
              path="*" 
              element={<SmartRedirect />}
            />
          </Routes>
        </React.Suspense>
       </main>
      
      {/* =============================================== */}
      {/* 🍞 SISTEMA DE NOTIFICAÇÕES TOAST */}
      {/* =============================================== */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
            fontSize: '14px',
            borderRadius: '12px',
            padding: '12px 16px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            fontWeight: '500',
            maxWidth: '400px',
          },
          success: {
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
            style: {
              background: 'linear-gradient(135deg, #10b981, #059669)',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
            style: {
              background: 'linear-gradient(135deg, #ef4444, #dc2626)',
            },
          },
          loading: {
            iconTheme: {
              primary: '#3b82f6',
              secondary: '#fff',
            },
            style: {
              background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
            },
          },
        }}
      />
    </div>
  );
};

// ===============================================
// 🎯 COMPONENTE PRINCIPAL DA APLICAÇÃO
// ===============================================
const App = () => {
  
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
};

// ===============================================
// 📋 CONFIGURAÇÕES E METADADOS
// ===============================================
App.displayName = 'InHoldingApp';
App.version = '3.3.0';


export default App;
